import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import RealtimeCodeEditor from '../components/RealtimeCodeEditor';
import SessionEndedModal from 'components/SessionEndedModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function CandidateView() {
  const { roomCode } = useParams();
  const [session, setSession] = useState(null);
  const [currentCode, setCurrentCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [hasJoined, setHasJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [sessionEndedModal, setSessionEndedModal] = useState({ open: false, message: '' });

  useEffect(() => {
    fetchSession();
  }, [roomCode]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/session/${roomCode}`);
      const sessionData = response.data.session;
      setSession(sessionData);

      // Load existing code if available
      if (sessionData.code_editor_data && sessionData.code_editor_data.length > 0) {
        // Get the latest code editor data
        const latestCodeData = sessionData.code_editor_data[0];
        setCurrentCode(latestCodeData.code_content);
      } else {
        setCurrentCode('-- Start coding here...');
      }

      // Check if session is ended
      if (sessionData.status === 'completed') {
        setError('This session has ended. Redirecting to main page...');
        setSessionEndedModal({ open: true, message: 'This session has ended. Redirecting to main page...' });
      }
      
      // If candidate name already exists, auto-join
      if (sessionData.candidate_name) {
        setCandidateName(sessionData.candidate_name);
        setHasJoined(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Code share session not found. Please check the link.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!candidateName.trim()) {
      setError('Please enter your name');
      return;
    }

    setJoining(true);
    setError('');

    try {
      await axios.post(`${API_URL}/public/session/${roomCode}/join`, {
        candidate_name: candidateName,
      });
      setHasJoined(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to join session. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !session) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  if (!hasJoined) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <VideoCallIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                Join Code Share Session
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You've been invited to a coding interview
              </Typography>
            </Box>

            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Company:
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {session?.company_name}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Interviewer:
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {session?.interviewer_name}
                </Typography>
              </CardContent>
            </Card>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleJoin}>
              <TextField
                fullWidth
                label="Your Name"
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
                margin="normal"
                required
                autoFocus
                placeholder="Enter your full name"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={joining}
                sx={{ mt: 3 }}
              >
                {joining ? 'Joining...' : 'Join Code Share'}
              </Button>
            </form>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
              By joining, you agree to participate in this code share session
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <>
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                Code Share with {session?.company_name}
              </Typography>
              {/* <Typography variant="body2">
                Candidate: {candidateName}
              </Typography> */}
            </Box>
            <Typography variant="body2">
              Room: {roomCode}
            </Typography>
          </Box>
        </Container>
      </Box>
      <Container maxWidth="xl" sx={{ flex: 1, py: 1, overflow: 'auto' }}>
        <RealtimeCodeEditor 
          room={roomCode} 
          userName={candidateName}
          userType="candidate"
          currentCode={currentCode}
          setCurrentCode={setCurrentCode}
        />
      </Container>
    </Box>
    <SessionEndedModal
      open={sessionEndedModal.open}
      message="This session has ended. Redirecting to main page..."
      onClose={() => window.location.href = '/'}
    />
  </>
  );
}

